package at.theatervogelweide.admin.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.Draggable
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.pointerevents.PointerEvent

private val reorder = Bem("admin-reorder")

private const val ITEM_ATTRIBUTE = "data-reorder-item"

/** How close to the top or bottom edge of the window a finger scrolls the page while dragging, in CSS pixels. */
private const val SCROLL_EDGE = 48.0

/** The fastest the page scrolls while dragging at the very edge, in CSS pixels per frame. */
private const val SCROLL_SPEED = 12.0

private var controllers = 0

/** Which arrow keys move an item, together with Alt. */
enum class ReorderAxis {
    /** Alt+↑ and Alt+↓, for lists. */
    VERTICAL,

    /** Alt+← and Alt+→ as well, for tiles that wrap. */
    BOTH,
}

/**
 * Sorting by hand, by drag and drop (mouse, touch and pen) and by keyboard (Alt+arrow keys on the focused handle),
 * for lists such as news items, images, roles and board members. The consumer keeps the order and changes it in
 * `onMove`; create it with [rememberReorder], mark each item with [reorderItem] and put a [ReorderHandle] into it. Wrap each
 * item in `key(…)`, so moving it moves its DOM node and the handle keeps focus.
 */
class ReorderController<K : Any> internal constructor() {
    internal var keys: List<K> = emptyList()
    internal var onMove: (from: Int, to: Int) -> Unit = { _, _ -> }
    internal var describe: (K) -> String = { it.toString() }
    internal var announce: ((K) -> String)? = null
    internal var axis: ReorderAxis = ReorderAxis.VERTICAL

    internal var dragged: K? by mutableStateOf(null)
    internal var over: K? by mutableStateOf(null)

    /** The handle to focus again once the moved item is in its new place; moving a DOM node drops its focus. */
    internal var focusAfterMove: K? by mutableStateOf(null)

    /** Read out by [ReorderAnnouncement] after a move. */
    internal var announcement: String by mutableStateOf("")

    /** Names the items in the DOM ([ITEM_ATTRIBUTE]), so a finger finds the item under it. */
    private val prefix = "reorder-${controllers++}-"
    private val ids = mutableMapOf<K, String>()
    private val byId = mutableMapOf<String, K>()

    internal fun idOf(key: K): String = ids.getOrPut(key) { (prefix + ids.size).also { byId[it] = key } }

    /** The touch or pen pointer that drags an item; mouse dragging uses the HTML drag and drop API. */
    private var pointerId: Int? = null
    private var pointerX = 0.0
    private var pointerY = 0.0
    private var scrollFrame: Int? = null

    internal val pointerDragging: Boolean get() = pointerId != null

    internal fun move(key: K, to: Int) {
        val from = keys.indexOf(key)
        if (from < 0 || to !in keys.indices || from == to) {
            return
        }

        onMove(from, to)
        focusAfterMove = key
        announcement = announce?.invoke(key) ?: "${describe(key)}: Position ${to + 1} von ${keys.size}"
    }

    internal fun moveBy(key: K, offset: Int) = move(key, keys.indexOf(key) + offset)

    private val onPointerMove: (Event) -> Unit = { event ->
        if ((event as PointerEvent).pointerId == pointerId) {
            followPointer(event)
        }
    }
    private val onPointerUp: (Event) -> Unit = { endPointerDrag(it as PointerEvent, dropped = true) }
    private val onPointerCancel: (Event) -> Unit = { endPointerDrag(it as PointerEvent, dropped = false) }

    internal fun startPointerDrag(key: K, handle: HTMLElement, event: PointerEvent) {
        if (pointerId != null) {
            return
        }
        pointerId = event.pointerId
        // keeps the finger's events coming while it leaves the handle; the window listeners below get them anyway
        runCatching { handle.setPointerCapture(event.pointerId) }
        window.addEventListener("pointermove", onPointerMove)
        window.addEventListener("pointerup", onPointerUp)
        window.addEventListener("pointercancel", onPointerCancel)
        dragged = key
        over = null
        followPointer(event)
        scrollFrame = window.requestAnimationFrame(::scrollNearEdge)
    }

    private fun endPointerDrag(event: PointerEvent, dropped: Boolean) {
        if (event.pointerId != pointerId) {
            return
        }
        pointerId = null
        window.removeEventListener("pointermove", onPointerMove)
        window.removeEventListener("pointerup", onPointerUp)
        window.removeEventListener("pointercancel", onPointerCancel)
        scrollFrame?.let { window.cancelAnimationFrame(it) }
        scrollFrame = null
        if (dropped) {
            // where the finger lifts, which may differ from its last move
            followPointer(event)
        }
        val target = over
        if (dropped && target != null) {
            drop(target)
        } else {
            dragged = null
            over = null
        }
    }

    private fun followPointer(event: PointerEvent) {
        pointerX = event.clientX.toDouble()
        pointerY = event.clientY.toDouble()
        updateOver()
    }

    /** The item under the finger, or none outside this list, so releasing there leaves the order. */
    private fun updateOver() {
        val item = document.elementFromPoint(pointerX, pointerY)?.closest("[$ITEM_ATTRIBUTE]")
        over = item?.getAttribute(ITEM_ATTRIBUTE)?.let { byId[it] }
    }

    /** Scrolls the page while the finger rests near the top or bottom edge, so long lists fit on a small screen. */
    private fun scrollNearEdge(@Suppress("UNUSED_PARAMETER") time: Double) {
        if (pointerId == null) {
            return
        }
        val height = window.innerHeight.toDouble()
        val speed = when {
            pointerY < SCROLL_EDGE -> -SCROLL_SPEED * (SCROLL_EDGE - pointerY) / SCROLL_EDGE
            pointerY > height - SCROLL_EDGE -> SCROLL_SPEED * (pointerY - height + SCROLL_EDGE) / SCROLL_EDGE
            else -> 0.0
        }
        if (speed != 0.0) {
            window.scrollBy(0.0, speed)
            updateOver()
        }
        scrollFrame = window.requestAnimationFrame(::scrollNearEdge)
    }

    internal fun drop(target: K) {
        val source = dragged ?: return
        dragged = null
        over = null
        move(source, keys.indexOf(target))
    }
}

/**
 * [keys] are the items in their current order; [describe] names an item for the screen reader announcement after a
 * move ("Probenstart: Position 2 von 5"), unless [announce] words it itself; it runs after [onMove], so it sees the new
 * order. [onMove] gets indices into [keys].
 */
@Composable
fun <K : Any> rememberReorder(
    keys: List<K>,
    describe: (K) -> String,
    axis: ReorderAxis = ReorderAxis.VERTICAL,
    announce: ((K) -> String)? = null,
    onMove: (from: Int, to: Int) -> Unit,
): ReorderController<K> {
    val controller = remember { ReorderController<K>() }
    val currentOnMove by rememberUpdatedState(onMove)
    controller.keys = keys
    controller.describe = describe
    controller.axis = axis
    controller.announce = announce
    controller.onMove = { from, to -> currentOnMove(from, to) }
    return controller
}

/**
 * Makes the element the drop target of the item [key]. It carries `data-over="true"` while another item is dragged
 * over it and `data-dragging="true"` while it is dragged itself, which the styles read.
 */
fun <K : Any> AttrsScope<*>.reorderItem(controller: ReorderController<K>, key: K) {
    attr(ITEM_ATTRIBUTE, controller.idOf(key))
    if (controller.dragged == key) {
        attr("data-dragging", "true")
    }
    if (controller.over == key && controller.dragged != key) {
        attr("data-over", "true")
    }
    onDragOver { event ->
        if (controller.dragged != null) {
            event.preventDefault()
            event.dataTransfer?.dropEffect = "move"
            controller.over = key
        }
    }
    onDragLeave {
        if (controller.over == key) {
            controller.over = null
        }
    }
    onDrop { event ->
        if (controller.dragged != null) {
            event.preventDefault()
            controller.drop(key)
        }
    }
}

/**
 * The grip of an item: drag it with the mouse or a finger, or focus it and press Alt+↑/↓ (and Alt+←/→ for [ReorderAxis.BOTH]).
 * [label] names the item, e.g. "Probenstart verschieben". [onImage] places it on an image tile.
 */
@Composable
fun <K : Any> ReorderHandle(controller: ReorderController<K>, key: K, label: String, onImage: Boolean = false) {
    var element by remember { mutableStateOf<HTMLElement?>(null) }

    // read during composition, so this handle recomposes after its move and the effect sees the DOM in place
    val focusNow = controller.focusAfterMove == key
    SideEffect {
        if (focusNow) {
            controller.focusAfterMove = null
            element?.focus()
        }
    }

    Button({
        type(ButtonType.Button)
        classes(*listOfNotNull(reorder.element("handle").className, reorder.element("handle").modifier("on-image").takeIf { onImage }).toTypedArray())
        attr("aria-label", label)
        attr("title", "Ziehen oder mit Alt und den Pfeiltasten verschieben")
        draggable(Draggable.True)
        ref {
            element = it
            onDispose { element = null }
        }
        // touch browsers don't start a drag and drop from a finger, they scroll; `touch-action: none` in the styles
        // keeps the gesture here, and the pointer events below move the item
        addEventListener("pointerdown") { event ->
            val pointer = event.nativeEvent as PointerEvent
            val handle = element
            if (pointer.pointerType != "mouse" && pointer.isPrimary && handle != null) {
                controller.startPointerDrag(key, handle, pointer)
            }
        }
        onDragStart { event ->
            // a long press may start a drag and drop as well (Chrome on Android); the pointer drag has it already
            if (controller.pointerDragging) {
                event.preventDefault()
                return@onDragStart
            }
            controller.dragged = key
            event.dataTransfer?.let { transfer ->
                transfer.effectAllowed = "move"
                // Firefox only starts a drag with data
                transfer.setData("text/plain", label)
                val item = (event.target as? HTMLElement)?.closest("[$ITEM_ATTRIBUTE]") as? HTMLElement
                item?.let { transfer.setDragImage(it, 16, 16) }
            }
        }
        onDragEnd {
            controller.dragged = null
            controller.over = null
        }
        onKeyDown { event ->
            if (!event.altKey) {
                return@onKeyDown
            }
            val offset = when (event.key) {
                "ArrowUp" -> -1
                "ArrowDown" -> 1
                "ArrowLeft" -> if (controller.axis == ReorderAxis.BOTH) -1 else 0
                "ArrowRight" -> if (controller.axis == ReorderAxis.BOTH) 1 else 0
                else -> 0
            }
            if (offset != 0) {
                event.preventDefault()
                controller.moveBy(key, offset)
            }
        }
    }) {
        Span({ attr("aria-hidden", "true") }) { Text("⋮⋮") }
    }
}

/** A visually hidden live region that reads out the new position after each move. */
@Composable
fun ReorderAnnouncement(controller: ReorderController<*>) {
    Span({
        classes("admin-visually-hidden")
        attr("aria-live", "polite")
    }) { Text(controller.announcement) }
}
